import {Button} from '@bosch/react-frok';
import React, {useEffect, useState} from 'react';
import {ActionBar, ActionButton, DialogWrapper, FileImages, PreviewWrapper} from './PreviewDialog.style';
import {StorageObject} from '../../../models/StorageObject';
import {t} from 'i18next';
import {getLabel, isFile} from '../../../utils/StorageObjectUtils';
import {useGetPreview} from '../../../hooks/UseGetPreview';
import {isFileDownloadable} from '../../../utils/DtoUtils';
import {executeAction, getFontSize, isNullOrEmpty} from '../../../utils';
import {generateIdentifier} from '../../../utils/IdentityUtils';
import {TemplatePropertySettings} from '../../../models/TemplatePropertySettings';
import {getApplicableTemplateMetadataPropertySettingsMap, isVisibleComponent} from '../../../utils/TemplateUtils';
import {ObjectType} from '../../../models/ObjectType';
import {STORAGE_OBJECT_OBJECT_LABELS, STORAGE_OBJECT_OBJECT_LABELS_NAME} from '../../../init/storage_object_constants';

export interface PreviewDialogProps {
    readonly item: StorageObject;
    readonly language: string;
    readonly defaultLocale: string;
    readonly editMode: boolean;
    editAction: () => void;
    deleteAction: () => void;
    downloadAction: () => void;
    onClose: () => void;
}

/**
 * @name PreviewDialog
 * @description
 * The dialog containing information to the file that has been clicked
 */
export const PreviewDialog: React.VFC<PreviewDialogProps> = (props) => {
    const [templatePropertySettings] = useState<Map<string, TemplatePropertySettings>>(getApplicableTemplateMetadataPropertySettingsMap(ObjectType.FILE, props.item.templateId));
    const isFileItem = isFile(props.item.objectType);
    const getPreview = useGetPreview();
    const [previewUrl, setPreviewUrl] = useState('');

    const setExistingPreview = async (id: string) => {
        let existingPreview = await getPreview(id);
        setPreviewUrl(existingPreview);
    };

    useEffect(() => {
        if ((props.item.id) && (!isNullOrEmpty(props.item.id))) {
            setExistingPreview(props.item.id).catch();
        }
    }, [props.item.id]);

    let label = getLabel(props.defaultLocale, props.item.labels);
    let isDownloadable = (isFileItem && isFileDownloadable(props.item.status)) as boolean;
    let fontSize = getFontSize([
        t('GENERAL_DOWNLOAD_LABEL'),
    ], 4);

    return (
        <DialogWrapper id={generateIdentifier()}
                       key={props.item.id}
                       modal
                       open
                       title={t('FILE_PREVIEW_DIALOG_TITLE')}
                       headline={(isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS, templatePropertySettings)
                           && isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS_NAME, templatePropertySettings)
                           && label.name) ? label.name : t('GENERAL_UNKNOWN_CONTENT_TEXT')}
                       headlineSpace={props.editMode}
                       onClose={props.onClose}
                       confirmButton={isDownloadable ?
                           <ActionButton id={generateIdentifier()}
                                         key='download'
                                         icon='download'
                                         mode='primary'
                                         type='button'
                                         label={t('GENERAL_DOWNLOAD_LABEL')}
                                         title={t('GENERAL_DOWNLOAD_LABEL')}
                                         fontSize={fontSize}
                           /> : undefined}
                       onConfirm={(e) => isDownloadable ? executeAction(e, props.downloadAction) : props.onClose}>
            {props.editMode && (
                <ActionBar id={generateIdentifier()}
                           key={'command-bar'}
                           actions={[
                               <Button id={generateIdentifier()}
                                       key='edit'
                                       icon='edit'
                                       mode='tertiary'
                                       type='button'
                                       title={t('GENERAL_EDIT_LABEL')}
                                       onClick={props.editAction}/>,
                               <Button id={generateIdentifier()}
                                       key='delete'
                                       icon='delete'
                                       mode='tertiary'
                                       type='button'
                                       title={t('GENERAL_DELETE_LABEL')}
                                       onClick={props.deleteAction}/>
                           ]}
                />
            )}
            <PreviewWrapper id={generateIdentifier()}>
                <FileImages id={generateIdentifier()}
                            src={previewUrl}
                            alt={'preview not found'}/>
            </PreviewWrapper>
        </DialogWrapper>
    );
};
